import {
  canInGuild,
  canManageUser,
  PermissionFlags,
} from '@alternativamais/chat-core';
import { useNavigate } from 'react-router';

import { useModoDesenvolvedor } from '../../../chat/ganchos';
import { avisarErro } from '../../../componentes/Avisos';
import {
  CaixaMenu,
  Flutuante,
  ItemMenu,
  type Posicao,
  SeparadorMenu,
} from '../../../componentes/Flutuante';
import { abrirCamada } from '../../../chat/camadas';
import { useCliente, useEstado, useEstadoRaso } from '../../../chat/contexto';
import { avisarCopiado } from './perfil';

/** Pede ao compositor da conversa aberta para inserir um texto (menção). */
export function inserirNoCompositor(canalId: string, texto: string) {
  window.dispatchEvent(
    new CustomEvent('chat:inserir', { detail: { canalId, texto } }),
  );
}

/**
 * Menu do membro (C19, botão direito na lista): perfil, mencionar,
 * mensagem, nota, cargos, apelido, castigar, expulsar, banir e copiar ID
 * (este só no modo desenvolvedor), conforme as permissões.
 */
export function MenuMembro({
  userId,
  guildId,
  canalId,
  ancora,
  aoFechar,
}: {
  userId: string;
  guildId: string | null;
  /** Conversa aberta (para "Mencionar"). */
  canalId?: string;
  ancora: Posicao;
  aoFechar: () => void;
}) {
  const dev = useModoDesenvolvedor();
  const cliente = useCliente();
  const navegar = useNavigate();
  const usuario = useEstado((estado) => estado.users[userId]);
  const eu = useEstado((estado) => estado.me?.id === userId);
  const pode = useEstadoRaso((estado) => {
    const gerencia = !!guildId && !eu && canManageUser(estado, guildId, userId);
    const tem = (flag: bigint) =>
      !!guildId && canInGuild(estado, guildId, flag);
    return {
      cargos: !!guildId && tem(PermissionFlags.MANAGE_ROLES),
      castigar: gerencia && tem(PermissionFlags.MODERATE_MEMBERS),
      expulsar: gerencia && tem(PermissionFlags.KICK_MEMBERS),
      banir: gerencia && tem(PermissionFlags.BAN_MEMBERS),
    };
  });
  if (!usuario) return null;
  const moderar = (acao: 'castigar' | 'expulsar' | 'banir') => () => {
    aoFechar();
    if (guildId) abrirCamada({ tipo: 'moderar', acao, guildId, userId });
  };
  const perfil = () => abrirCamada({ tipo: 'perfil', userId, guildId, ancora });
  return (
    <Flutuante
      ancora={ancora}
      aoFechar={aoFechar}
      rotulo={`Opções de ${usuario.username}`}
      papel="menu"
    >
      <CaixaMenu largura={220}>
        <ItemMenu
          icone="usuario"
          aoEscolher={() => {
            aoFechar();
            perfil();
          }}
        >
          Perfil
        </ItemMenu>
        {canalId && (
          <ItemMenu
            icone="arroba"
            aoEscolher={() => {
              aoFechar();
              inserirNoCompositor(canalId, `@${usuario.username}`);
            }}
          >
            Mencionar
          </ItemMenu>
        )}
        {!eu && !usuario.bot && (
          <ItemMenu
            icone="responder"
            aoEscolher={() => {
              aoFechar();
              cliente
                .openDm(userId)
                .then((dm) => navegar(`/canais/@me/${dm.id}`))
                .catch(avisarErro);
            }}
          >
            Mensagem
          </ItemMenu>
        )}
        {!eu && (
          <ItemMenu
            icone="editar"
            aoEscolher={() => {
              aoFechar();
              perfil();
            }}
          >
            Adicionar nota
          </ItemMenu>
        )}
        {pode.cargos && guildId && (
          <ItemMenu
            icone="escudo"
            aoEscolher={() => {
              aoFechar();
              abrirCamada({
                tipo: 'config-servidor',
                guildId,
                secao: 'membros',
                alvo: userId,
              });
            }}
          >
            Cargos
          </ItemMenu>
        )}
        {(pode.castigar || pode.expulsar || pode.banir) && <SeparadorMenu />}
        {pode.castigar && (
          <ItemMenu icone="relogio" aoEscolher={moderar('castigar')}>
            Castigar {usuario.global_name ?? usuario.username}
          </ItemMenu>
        )}
        {pode.expulsar && (
          <ItemMenu perigo icone="sair" aoEscolher={moderar('expulsar')}>
            Expulsar {usuario.global_name ?? usuario.username}
          </ItemMenu>
        )}
        {pode.banir && (
          <ItemMenu perigo icone="banir" aoEscolher={moderar('banir')}>
            Banir {usuario.global_name ?? usuario.username}
          </ItemMenu>
        )}
        {dev && <SeparadorMenu />}
        {dev && (
          <ItemMenu
            icone="copiar"
            aoEscolher={() => {
              aoFechar();
              avisarCopiado(userId, 'ID copiado.');
            }}
          >
            Copiar ID do usuário
          </ItemMenu>
        )}
      </CaixaMenu>
    </Flutuante>
  );
}
